/**
 * SEO Utility Functions
 * Centralized SEO configuration and helpers
 */

export const siteConfig = {
  siteName: 'Randiv Dulneth Portfolio',
  siteUrl: 'https://mr-randiv.vercel.app',
  author: 'Randiv Dulneth',
  authorAlias: 'MrRandiv',
  email: 'contact@mr-randiv.vercel.app',
  location: 'Kandy, Sri Lanka',
  description: 'Randiv Dulneth (MrRandiv, Dulneth Randiv) - Software Engineer & SLIIT Student. Portfolio showcasing Full-Stack Development, MERN Stack projects, React, Node.js applications. Based in Kandy, Sri Lanka.',
  keywords: 'Randiv, Randiv Dulneth, MrRandiv, Mr Randiv, Dulneth, Dulneth Randiv, SLIIT, SLIIT Student, Randiv SLIIT, Dulneth SLIIT, Software Engineer Randiv, Full-Stack Developer Randiv Dulneth, MERN Stack Developer, React Developer Sri Lanka, Node.js Developer, Randiv Portfolio, MrRandiv Portfolio, Dulneth Portfolio, ICT Student SLIIT, Web Developer Kandy, Sri Lanka Developer, Undergraduate Developer, Randiv GitHub, Randiv LinkedIn, Software Engineer Sri Lanka, Kandy Developer',
  education: 'Sri Lanka Institute of Information Technology (SLIIT)',
  degree: 'Diploma in Information Technology',
  specialization: 'Information and Communication Technology (ICT)',
  social: {
    twitter: '@mrrandiv',
    github: 'https://github.com/mrrandiv',
    linkedin: 'https://linkedin.com/in/randiv-dulneth',
  },
  ogImage: '/og-image.jpg',
  twitterCard: 'summary_large_image',
};

/**
 * Generate page-specific SEO metadata
 */
export const generatePageMeta = (page) => {
  const baseUrl = siteConfig.siteUrl;
  
  const pages = {
    home: {
      title: `Randiv Dulneth (MrRandiv) - Software Engineer | SLIIT Student | Full-Stack Developer Portfolio`,
      description: `Randiv Dulneth (MrRandiv, Dulneth Randiv) - Software Engineer & SLIIT Student. Dulneth specializes in Full-Stack Development, MERN Stack, React, Node.js. Portfolio showcasing innovative web applications. Based in Kandy, Sri Lanka.`,
      canonical: baseUrl,
      keywords: `${siteConfig.keywords}, Randiv, MrRandiv, Dulneth, SLIIT, Software Engineer`,
    },
    about: {
      title: `About Randiv Dulneth (MrRandiv) - SLIIT ICT Student & Software Engineer`,
      description: `About Randiv Dulneth (MrRandiv, Dulneth Randiv) - SLIIT student pursuing Diploma in IT with ICT specialization. Passionate Full-Stack Developer specializing in MERN Stack, React, Node.js. Based in Kandy, Sri Lanka.`,
      canonical: `${baseUrl}/about`,
      keywords: `Randiv, Randiv Dulneth, MrRandiv, Dulneth, SLIIT, About Randiv, Biography, Skills, Education, SLIIT ICT, Software Engineer, Kandy`,
    },
    projects: {
      title: `Projects - ${siteConfig.author} Portfolio`,
      description: `Explore the portfolio of ${siteConfig.author}, featuring full-stack web applications, React projects, and innovative solutions.`,
      canonical: `${baseUrl}/projects`,
      keywords: `${siteConfig.keywords}, Projects, Portfolio, Web Applications, React Apps`,
    },
    contact: {
      title: `Contact ${siteConfig.author} - Get In Touch`,
      description: `Get in touch with ${siteConfig.author} for networking, internship inquiries, or professional opportunities.`,
      canonical: `${baseUrl}/contact`,
      keywords: `${siteConfig.keywords}, Contact, Networking, Connect, Email`,
    },
  };

  return pages[page] || pages.home;
};

/**
 * Generate Open Graph metadata
 */
export const generateOGMeta = (page) => {
  const meta = generatePageMeta(page);
  const baseUrl = siteConfig.siteUrl;
  
  return {
    'og:type': 'website',
    'og:url': meta.canonical,
    'og:title': meta.title,
    'og:description': meta.description,
    'og:image': `${baseUrl}${siteConfig.ogImage}`,
    'og:site_name': siteConfig.siteName,
    'og:locale': 'en_US',
  };
};

/**
 * Generate Twitter Card metadata
 */
export const generateTwitterMeta = (page) => {
  const meta = generatePageMeta(page);
  const baseUrl = siteConfig.siteUrl;
  
  return {
    'twitter:card': siteConfig.twitterCard,
    'twitter:url': meta.canonical,
    'twitter:title': meta.title,
    'twitter:description': meta.description,
    'twitter:image': `${baseUrl}${siteConfig.ogImage}`,
    'twitter:creator': siteConfig.social.twitter,
    'twitter:site': siteConfig.social.twitter,
  };
};

/**
 * Generate JSON-LD structured data for Organization
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.siteName,
  url: siteConfig.siteUrl,
  logo: `${siteConfig.siteUrl}/logo-512.png?v=2`,
  sameAs: [
    siteConfig.social.github,
    siteConfig.social.linkedin,
  ],
});

/**
 * Generate JSON-LD structured data for WebSite with search
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteConfig.siteName,
  alternateName: `${siteConfig.authorAlias} Portfolio`,
  url: siteConfig.siteUrl,
  description: siteConfig.description,
  author: {
    '@type': 'Person',
    name: siteConfig.author,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteConfig.siteUrl}/projects?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Generate JSON-LD structured data for Person
 */
export const generatePersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: siteConfig.author,
  alternateName: siteConfig.authorAlias,
  url: siteConfig.siteUrl,
  image: `${siteConfig.siteUrl}/profile-image.jpg`,
  sameAs: [
    siteConfig.social.github,
    siteConfig.social.linkedin,
  ],
  jobTitle: 'Software Developer & Student',
  affiliation: {
    '@type': 'EducationalOrganization',
    name: 'Sri Lanka Institute of Information Technology (SLIIT)',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Kandy',
    addressCountry: 'Sri Lanka',
  },
  knowsAbout: [
    'React.js',
    'Node.js',
    'MongoDB',
    'Express.js',
    'JavaScript',
    'TypeScript',
    'Full-Stack Development',
    'MERN Stack',
    'Web Development',
    'RESTful APIs',
    'Git',
    'Agile Development',
  ],
  description: `Randiv Dulneth (MrRandiv, Dulneth Randiv, Dulneth) is a Software Engineer and undergraduate student at SLIIT (Sri Lanka Institute of Information Technology) pursuing a Diploma in IT with specialization in ICT. Based in Kandy, Sri Lanka, Randiv is passionate about full-stack development, specializing in MERN stack, React, Node.js, and creating innovative web applications with modern technologies. Connect with Dulneth Randiv on GitHub, LinkedIn, and Twitter.`,
});

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${siteConfig.siteUrl}${item.path}`,
  })),
});
